import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { LeaseRecurringPaymentRepository } from '~/repositories/leaseRecurringPaymentRepository';
import type { Accessor } from 'solid-js';
import type { CreateLeaseRecurringPaymentDto, RecurringLeasePaymentDto, UpdateLeaseRecurringPaymentDto } from '~/swagger/portal/Api';

interface LeaseRentPaymentValue {
  payments: Accessor<RecurringLeasePaymentDto[]>;
  loading: Accessor<boolean>;
  error: Accessor<Error | undefined>;
  getRentPayment: (leaseId: string) => Promise<void>;
  deleteRentPayment: (leaseId: string, id: string, deleteAllPayments: boolean) => Promise<void>;
  updateRentPayment: (leaseId: string, recurringId: string, payload: UpdateLeaseRecurringPaymentDto) => Promise<void>;
  addRentPayment: (leaseId: string, payload: CreateLeaseRecurringPaymentDto) => Promise<RecurringLeasePaymentDto | undefined>;
}

export const [LeaseRentPaymentProvider, useLeaseRentPayment] = createMagicDoorContext<LeaseRentPaymentValue>('LeaseRentPayment', () => {
  const repo = new LeaseRecurringPaymentRepository();

  const [payments, setPayments] = createSignal<RecurringLeasePaymentDto[]>([]);
  const [loading, setLoading] = createSignal<boolean>(false);
  const [error, setError] = createSignal<Error | undefined>(undefined);

  const getRentPayment = async (id: string) => {
    setLoading(true);
    try {
      const response = await repo.getRentPayment(id);
      setPayments(response.recurringPayments);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const addRentPayment = async (id: string, payload: CreateLeaseRecurringPaymentDto): Promise<RecurringLeasePaymentDto | undefined> => {
    try {
      const response = await repo.addRentPayment(id, payload);
      getRentPayment(id);
      return response;
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const updateRentPayment = async (leaseId: string, recurringId: string, payload: UpdateLeaseRecurringPaymentDto): Promise<void> => {
    try {
      await repo.updateRentPayment(leaseId, recurringId, payload);
      getRentPayment(leaseId);
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const deleteRentPayment = async (leaseId: string, recurringId: string, deleteAllPayments: boolean) => {
    try {
      const response = await repo.deleteRentPayment(leaseId, recurringId, { deleteAllPayments });
      setPayments((prev: RecurringLeasePaymentDto[]) => prev.filter((recurring) => recurring.id !== recurringId));
      return response;
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  return {
    payments,
    loading,
    error,
    getRentPayment,
    addRentPayment,
    updateRentPayment,
    deleteRentPayment,
  };
});
